import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Grid,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Chip,
  Avatar,
  InputAdornment,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  AccessTime as TimeIcon,
  Notes as NotesIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { RootState, AppDispatch } from '../../store';
import { bookAppointment, resetBookingSuccess } from '../../store/slices/appointmentSlice';
import { fetchDoctors } from '../../store/slices/doctorSlice';
import { fetchOfficerProfile } from '../../store/slices/officerSlice';
import { showSuccess, showError } from '../../store/slices/uiSlice';

/**
 * Book Appointment Page
 * Premium appointment booking form for Civilhealth Healthcare System.
 *
 * @author Civilhealth Development Team
 */

interface Doctor {
  id: number;
  userId?: number;
  username?: string;
  email?: string;
  mobile?: string;
  fullName?: string;
  name?: string;
  registrationNumber?: string;
  qualification: string;
  specialization: string;
  department?: string;
  experienceYears?: number;
  experience?: number;
  status?: string;
  statusDisplayName?: string;
  hospitalName?: string;
  rating: number;
  totalReviews?: number;
  totalPatientsServed?: number;
  verified?: boolean;
}

// Predefined patient types - simple dropdown without database
const patientTypes = [
  { value: 'self', label: 'Self (मैं स्वयं)' },
  { value: 'spouse', label: 'Spouse (पत्नी/पति)' },
  { value: 'son', label: 'Son (पुत्र)' },
  { value: 'daughter', label: 'Daughter (पुत्री)' },
  { value: 'father', label: 'Father (पिता)' },
  { value: 'mother', label: 'Mother (माता)' },
  { value: 'brother', label: 'Brother (भाई)' },
  { value: 'sister', label: 'Sister (बहन)' },
  { value: 'other', label: 'Other (अन्य)' },
];

const validationSchema = yup.object({
  patientType: yup.string().required('Please select who this appointment is for'),
  patientName: yup.string().when('patientType', {
    is: (value: string) => value !== 'self',
    then: (schema) => schema.required('Please enter patient name'),
    otherwise: (schema) => schema.notRequired(),
  }),
  doctorId: yup.number().required('Please select a doctor'),
  appointmentDate: yup.date()
    .required('Please select appointment date')
    .min(new Date(new Date().setHours(0, 0, 0, 0)), 'Appointment date cannot be in the past'),
  appointmentTime: yup.string().required('Please select appointment time'),
  appointmentType: yup.string().required('Please select appointment type'),
  symptoms: yup.string(),
  notes: yup.string(),
});

const BookAppointmentPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const { doctors, loading: doctorsLoading } = useSelector((state: RootState) => state.doctor);
  const { currentOfficer, loading: officerLoading } = useSelector((state: RootState) => state.officer);
  const { loading: appointmentLoading, error: appointmentError, bookingSuccess } = useSelector(
    (state: RootState) => state.appointment
  );

  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);

  // Time slots (24-hour format - full day)
  const timeSlots = [
    { value: '00:00', label: '00:00 (12:00 AM - Midnight)' },
    { value: '00:30', label: '00:30 (12:30 AM)' },
    { value: '01:00', label: '01:00 (1:00 AM)' },
    { value: '01:30', label: '01:30 (1:30 AM)' },
    { value: '02:00', label: '02:00 (2:00 AM)' },
    { value: '02:30', label: '02:30 (2:30 AM)' },
    { value: '03:00', label: '03:00 (3:00 AM)' },
    { value: '03:30', label: '03:30 (3:30 AM)' },
    { value: '04:00', label: '04:00 (4:00 AM)' },
    { value: '04:30', label: '04:30 (4:30 AM)' },
    { value: '05:00', label: '05:00 (5:00 AM)' },
    { value: '05:30', label: '05:30 (5:30 AM)' },
    { value: '06:00', label: '06:00 (6:00 AM)' },
    { value: '06:30', label: '06:30 (6:30 AM)' },
    { value: '07:00', label: '07:00 (7:00 AM)' },
    { value: '07:30', label: '07:30 (7:30 AM)' },
    { value: '08:00', label: '08:00 (8:00 AM)' },
    { value: '08:30', label: '08:30 (8:30 AM)' },
    { value: '09:00', label: '09:00 (9:00 AM)' },
    { value: '09:30', label: '09:30 (9:30 AM)' },
    { value: '10:00', label: '10:00 (10:00 AM)' },
    { value: '10:30', label: '10:30 (10:30 AM)' },
    { value: '11:00', label: '11:00 (11:00 AM)' },
    { value: '11:30', label: '11:30 (11:30 AM)' },
    { value: '12:00', label: '12:00 (12:00 PM - Noon)' },
    { value: '12:30', label: '12:30 (12:30 PM)' },
    { value: '13:00', label: '13:00 (1:00 PM)' },
    { value: '13:30', label: '13:30 (1:30 PM)' },
    { value: '14:00', label: '14:00 (2:00 PM)' },
    { value: '14:30', label: '14:30 (2:30 PM)' },
    { value: '15:00', label: '15:00 (3:00 PM)' },
    { value: '15:30', label: '15:30 (3:30 PM)' },
    { value: '16:00', label: '16:00 (4:00 PM)' },
    { value: '16:30', label: '16:30 (4:30 PM)' },
    { value: '17:00', label: '17:00 (5:00 PM)' },
    { value: '17:30', label: '17:30 (5:30 PM)' },
    { value: '18:00', label: '18:00 (6:00 PM)' },
    { value: '18:30', label: '18:30 (6:30 PM)' },
    { value: '19:00', label: '19:00 (7:00 PM)' },
    { value: '19:30', label: '19:30 (7:30 PM)' },
    { value: '20:00', label: '20:00 (8:00 PM)' },
    { value: '20:30', label: '20:30 (8:30 PM)' },
    { value: '21:00', label: '21:00 (9:00 PM)' },
    { value: '21:30', label: '21:30 (9:30 PM)' },
    { value: '22:00', label: '22:00 (10:00 PM)' },
    { value: '22:30', label: '22:30 (10:30 PM)' },
    { value: '23:00', label: '23:00 (11:00 PM)' },
    { value: '23:30', label: '23:30 (11:30 PM)' },
  ];

  const appointmentTypes = [
    { value: 'HOME_VISIT', label: 'Home Visit' },
    { value: 'VIDEO_CONSULTATION', label: 'Video Consultation' },
    { value: 'TELEPHONE_CONSULTATION', label: 'Telephone Consultation' },
    { value: 'EMERGENCY_VISIT', label: 'Emergency Visit' },
    { value: 'FOLLOW_UP', label: 'Follow-up' },
  ];

  // Fetch doctors on mount
  useEffect(() => {
    dispatch(fetchDoctors() as any);
  }, [dispatch]);

  // Fetch officer profile when user is available
  useEffect(() => {
    if (user?.id && !currentOfficer) {
      dispatch(fetchOfficerProfile(user.id) as any);
    }
  }, [user?.id, currentOfficer, dispatch]);

  useEffect(() => {
    if (bookingSuccess) {
      dispatch(showSuccess('Appointment booked successfully!'));
      dispatch(resetBookingSuccess());
      navigate('/appointments');
    }
  }, [bookingSuccess, dispatch, navigate]);

  useEffect(() => {
    if (appointmentError) {
      dispatch(showError(appointmentError));
    }
  }, [appointmentError, dispatch]);

  const formik = useFormik({
    initialValues: {
      patientType: 'self',
      patientName: '',
      doctorId: '',
      appointmentDate: '',
      appointmentTime: '',
      appointmentType: 'HOME_VISIT',
      symptoms: '',
      notes: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (!user?.id) {
        dispatch(showError('You must be logged in to book an appointment'));
        return;
      }

      const patientTypeLabel = patientTypes.find(p => p.value === values.patientType)?.label || values.patientType;
      const finalPatientName = values.patientType === 'self'
        ? user.fullName
        : values.patientName;

      const appointmentData = {
        doctorId: Number(values.doctorId),
        appointmentDate: values.appointmentDate,
        appointmentTime: values.appointmentTime,
        appointmentType: values.appointmentType,
        symptoms: values.symptoms,
        notes: values.notes,
        patientType: values.patientType,
        patientName: finalPatientName,
        patientMobile: user.mobile,
        patientEmail: user.email,
      };

      // Get officer ID from the fetched officer profile
      const officerId = currentOfficer?.id;
      if (!officerId) {
        dispatch(showError('Officer profile not found. Please login again.'));
        return;
      }

      // Pass officerId along with appointment data
      dispatch(bookAppointment({ officerId, data: appointmentData }) as any);
    },
  });

  const handleDoctorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const doctorId = Number(event.target.value);
    const doctor = doctors.find((d: any) => d.id === doctorId);
    setSelectedDoctor(doctor || null);
    formik.setFieldValue('doctorId', doctorId);
  };

  // Check if patient type requires name input
  const requiresNameInput = formik.values.patientType !== 'self';

  return (
    <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/appointments')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Book Appointment
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Form Section */}
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardHeader
              title="Appointment Details"
              subheader="Fill in the details to book your appointment"
              sx={{ borderBottom: '1px solid #e0e0e0' }}
            />
            <CardContent>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                  {/* Patient Selection */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      Who is this appointment for?
                    </Typography>
                    <TextField
                      fullWidth
                      select
                      id="patientType"
                      name="patientType"
                      label="Select Patient"
                      value={formik.values.patientType}
                      onChange={formik.handleChange}
                      error={formik.touched.patientType && Boolean(formik.errors.patientType)}
                      helperText={formik.touched.patientType && formik.errors.patientType}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    >
                      {patientTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Show patient info for Self */}
                  {formik.values.patientType === 'self' && (
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'transparent' , borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 48, height: 48, bgcolor: 'transparent' }}>
                            {user?.fullName?.charAt(0) || 'U'}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {user?.fullName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {user?.email} • {user?.mobile}
                            </Typography>
                            <Chip
                              label="Self"
                              size="small"
                              color="primary"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  )}

                  {/* Patient Name Input (for non-self options) */}
                  {requiresNameInput && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        id="patientName"
                        name="patientName"
                        label="Patient Name"
                        value={formik.values.patientName}
                        onChange={formik.handleChange}
                        error={formik.touched.patientName && Boolean(formik.errors.patientName)}
                        helperText={formik.touched.patientName && formik.errors.patientName}
                        placeholder={`Enter ${patientTypes.find(p => p.value === formik.values.patientType)?.label.split('(')[0].trim()} name`}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  )}

                  {/* Doctor Selection */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      select
                      id="doctorId"
                      name="doctorId"
                      label="Select Doctor"
                      value={formik.values.doctorId}
                      onChange={handleDoctorChange}
                      error={formik.touched.doctorId && Boolean(formik.errors.doctorId)}
                      helperText={formik.touched.doctorId && formik.errors.doctorId}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HospitalIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    >
                      {doctorsLoading ? (
                        <MenuItem disabled>Loading doctors...</MenuItem>
                      ) : (
                        (doctors || []).map((doctor: any) => (
                          <MenuItem key={doctor.id} value={doctor.id}>
                            {doctor.fullName || doctor.name} - {doctor.specialization}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  </Grid>

                  {/* Appointment Date & Time */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="appointmentDate"
                      name="appointmentDate"
                      label="Appointment Date"
                      type="date"
                      value={formik.values.appointmentDate}
                      onChange={formik.handleChange}
                      error={formik.touched.appointmentDate && Boolean(formik.errors.appointmentDate)}
                      helperText={formik.touched.appointmentDate && formik.errors.appointmentDate}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: new Date().toISOString().split('T')[0] }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      id="appointmentTime"
                      name="appointmentTime"
                      label="Appointment Time"
                      value={formik.values.appointmentTime}
                      onChange={formik.handleChange}
                      error={formik.touched.appointmentTime && Boolean(formik.errors.appointmentTime)}
                      helperText={formik.touched.appointmentTime && formik.errors.appointmentTime}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <TimeIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    >
                      {timeSlots.map((slot) => (
                        <MenuItem key={slot.value} value={slot.value}>
                          {slot.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Appointment Type */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      select
                      id="appointmentType"
                      name="appointmentType"
                      label="Appointment Type"
                      value={formik.values.appointmentType}
                      onChange={formik.handleChange}
                      error={formik.touched.appointmentType && Boolean(formik.errors.appointmentType)}
                      helperText={formik.touched.appointmentType && formik.errors.appointmentType}
                    >
                      {appointmentTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Symptoms */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      id="symptoms"
                      name="symptoms"
                      label="Describe your symptoms (Optional)"
                      value={formik.values.symptoms}
                      onChange={formik.handleChange}
                      error={formik.touched.symptoms && Boolean(formik.errors.symptoms)}
                      helperText={formik.touched.symptoms && formik.errors.symptoms}
                      placeholder="Describe your symptoms if any..."
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                            <NotesIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Notes */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      id="notes"
                      name="notes"
                      label="Additional Notes (Optional)"
                      value={formik.values.notes}
                      onChange={formik.handleChange}
                      placeholder="Any additional information for the doctor..."
                    />
                  </Grid>

                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={appointmentLoading}
                        startIcon={appointmentLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                        sx={{
                          minWidth: 150,
                          background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
                        }}
                      >
                        Book Appointment
                      </Button>
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={() => navigate('/appointments')}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* Doctor Info Section */}
        <Grid item xs={12} md={4}>
          {selectedDoctor ? (
            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', position: 'sticky', top: 20 }}>
              <CardHeader title="Doctor Information" />
              <Divider />
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ width: 60, height: 60, bgcolor: 'transparent' , fontSize: '1.5rem' }}>
                    {(selectedDoctor as any).fullName?.charAt(0) || 'D'}
                  </Avatar>
                  <Box sx={{ ml: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {(selectedDoctor as any).fullName || (selectedDoctor as any).name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedDoctor.specialization}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Qualification
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedDoctor.qualification}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Experience
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedDoctor.experience} years
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Rating
                    </Typography>
                    <Chip
                      label={`${selectedDoctor.rating || 4.5} / 5`}
                      color="primary"
                      size="small"
                    />
                  </Grid>
                </Grid>

                <Alert severity="info" sx={{ mt: 2 }}>
                  Please arrive 15 minutes before your appointment time.
                </Alert>
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <HospitalIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Select a Doctor
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Doctor information will appear here
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default BookAppointmentPage;